import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { EVENTS, useJoyride, type Step, type TooltipRenderProps } from 'react-joyride';
import { isRtl, tr } from './i18n';
import { useSession, useTenant } from './session';

/** Product tour of the tenant portal (react-joyride). Runs once per user per browser; replayable from the sidebar. */

const seenKey = (userId: string) => `zerogap.tour.${userId}`;

function hasSeen(userId: string): boolean {
  try {
    return localStorage.getItem(seenKey(userId)) === '1';
  } catch {
    return true; // storage blocked: never auto-start, the sidebar button still works
  }
}

function markSeen(userId: string) {
  try {
    localStorage.setItem(seenKey(userId), '1');
  } catch {
    /* storage blocked */
  }
}

/** Matches the breakpoint where styles.css collapses the sidebar behind the Menu button. */
const MOBILE_QUERY = '(max-width: 820px)';

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

/**
 * Let React commit the opened/closed menu before Joyride measures the target.
 * A timer, not requestAnimationFrame: rAF is paused while the tab is hidden, which would stall the
 * step until its beforeTimeout instead of a few milliseconds.
 */
const committed = () => new Promise<void>((resolve) => setTimeout(resolve, 60));

function TourTooltip({ index, size, step, isLastStep, backProps, closeProps, primaryProps, skipProps, tooltipProps, controls }: TooltipRenderProps) {
  return (
    <div
      {...tooltipProps}
      className="card tour-tip"
      aria-label={typeof step.title === 'string' ? step.title : undefined}
      onKeyDown={(e) => e.key === 'Escape' && controls.skip('button_close')}
    >
      <div className="tour-head">
        <span className="eyebrow">{tr('Step {n} of {total}', { n: index + 1, total: size })}</span>
        <button type="button" {...closeProps} className="tour-x">×</button>
      </div>
      {step.title && <h3 className="tour-title">{step.title}</h3>}
      <div className="tour-body">{step.content}</div>
      <div className="tour-foot">
        {!isLastStep && <button type="button" {...skipProps} className="btn sm ghost">{tr('Skip tour')}</button>}
        <span className="tour-spacer" />
        {index > 0 && <button type="button" {...backProps} className="btn sm">{tr('Back')}</button>}
        <button type="button" {...primaryProps} className="btn sm primary">{isLastStep ? tr('Finish') : tr('Next')}</button>
      </div>
    </div>
  );
}

const Ctx = createContext<{ available: boolean; start: () => void }>({ available: false, start: () => {} });

export const useTour = () => useContext(Ctx);

export function TourProvider({ children, onMenuChange }: { children: ReactNode; onMenuChange?: (open: boolean) => void }) {
  const { me } = useSession();
  const t = useTenant();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const available = !!me && !!t.tenantId && !t.support;
  const canManage = t.can('devices.manage');
  const canRestore = t.can('restores.create');
  const menu = useRef(onMenuChange);
  menu.current = onMenuChange;

  const steps = useMemo<Step[]>(() => {
    const side = isRtl() ? 'left' : 'right';
    // On mobile the sidebar lives behind the Menu button: open it for the steps that point at it,
    // and close it again for the steps that point at the page, so nothing waits on a hidden target.
    // The hook re-checks the viewport when it runs, so a missed media-query update can't strand a step.
    const setMenu = (open: boolean) => async () => {
      if (!window.matchMedia(MOBILE_QUERY).matches) return;
      menu.current?.(open);
      await committed();
    };
    const inMenu: Partial<Step> = { placement: isMobile ? 'bottom' : side, before: setMenu(true), targetWaitTimeout: 2000 };
    const onPage: Partial<Step> = { before: setMenu(false) };
    const list: (Step | false)[] = [
      { target: 'body', placement: 'center', title: tr('Welcome to ZeroGap'), content: tr('A one-minute look around. You can replay this tour any time from the bottom of the menu.'), ...onPage },
      { target: '[data-tour="protection"]', placement: 'bottom', title: tr('Everything at a glance'), content: tr('Here you see whether all your computers are backed up, and what to do if one needs attention.'), ...onPage },
      canManage && { target: '[data-tour="add-computer"]', placement: 'bottom', title: tr('Connect a computer'), content: tr('Start here: install ZeroGap on each computer you want to protect. It starts backing up on its own.'), ...onPage },
      { target: '[data-tour="nav-devices"]', title: tr('Computers'), content: tr('All your computers, whether each one is backed up, and when it was last backed up.'), ...inMenu },
      { target: '[data-tour="nav-plans"]', title: tr('Backup plans'), content: tr('A backup plan decides what gets backed up, when, and for how long. New computers get the default plan, so you only need to change it if you want to.'), ...inMenu },
      {
        target: '[data-tour="nav-restores"]', title: tr('Get files back'),
        content: canRestore
          ? tr('Bring back a file or folder as it was at an earlier backup, even if the computer is off or gone.')
          : tr('See what is backed up. Admins can get files back from here.'),
        ...inMenu,
      },
      { target: '[data-tour="nav-alerts"]', title: tr('Alerts'), content: tr('If something needs your attention, it shows up here and you get an email.'), ...inMenu },
      { target: '[data-tour="tour-replay"]', title: tr('Replay any time'), content: tr('Open this tour again from here whenever you need a refresher.'), ...inMenu, placement: 'top' },
    ];
    return list.filter((s): s is Step => !!s);
  }, [canManage, canRestore, isMobile]);

  const { controls, Tour } = useJoyride({
    steps,
    continuous: true,
    tooltipComponent: TourTooltip,
    onEvent: (data) => {
      if (data.type === EVENTS.TOUR_END) menu.current?.(false);
    },
    locale: { back: tr('Back'), close: tr('Close'), last: tr('Finish'), next: tr('Next'), skip: tr('Skip tour') },
    // Joyride's floater wrapper carries a drop-shadow filter, and any filter on an ancestor stops the
    // glass card inside from blurring the page behind it. The card draws its own shadow.
    styles: { floater: { filter: 'none' } },
    options: {
      buttons: ['back', 'close', 'primary', 'skip'],
      skipBeacon: true,
      closeButtonAction: 'skip',
      dismissKeyAction: false, // Escape is handled in the tooltip so it ends the tour instead of advancing
      overlayClickAction: false,
      overlayColor: 'rgba(12, 14, 20, 0.55)',
      arrowColor: 'var(--card)',
      primaryColor: '#b0802b',
      spotlightPadding: 6,
      spotlightRadius: 10,
      targetWaitTimeout: 5000, // dashboard cards render after their query resolves
      width: 360,
      zIndex: 60,
    },
  });

  const start = useCallback(() => {
    if (!available) return;
    markSeen(me!.user.id);
    if (pathname !== t.base) nav(t.base);
    controls.start(0);
  }, [available, me, pathname, t.base, nav, controls]);

  // First visit to a tenant dashboard: start once, then remember it for this user in this browser.
  const autoStart = available && pathname === t.base && !hasSeen(me!.user.id);
  useEffect(() => {
    if (autoStart) start();
  }, [autoStart, start]);

  const value = useMemo(() => ({ available, start }), [available, start]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {Tour}
    </Ctx.Provider>
  );
}
